import React, { useMemo } from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';

// --- Replicating External Helpers & Constants ---
const MONTHLY_COLUMNS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const formatNumber = (val) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// --- Border Color Constants ---
const DEFAULT_BORDER_COLOR_HEADER = '#ccc';
const DEFAULT_BORDER_COLOR_BODY = '#eee';
const DEFAULT_BORDER_COLOR_FOOTER = '#000';
// ------------------------------------------------

// Create base styles for the PDF
const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        padding: 10,
        fontSize: 7, // Small font needed for 15+ columns
    },
    table: {
        display: 'flex',
        width: 'auto',
        flexDirection: 'column',
        // borderWidth: 0, 
    },
    headerRow: {
        flexDirection: 'row',
        backgroundColor: '#e0f7fa',
        // borderBottomWidth: 1,
        // borderBottomColor: DEFAULT_BORDER_COLOR_HEADER,
        textAlign: 'center',
        fontWeight: 'bold',
    },
    row: {
        flexDirection: 'row',
        // borderBottomWidth: 1,
        // borderBottomColor: DEFAULT_BORDER_COLOR_BODY,
    },
    cell: {
        padding: 3,
        overflow: 'hidden', 
        justifyContent: 'center',
    },
    headerCell: {
        padding: 3,
        fontWeight: 'bold',
        justifyContent: 'center',
    },
    numeric: {
        textAlign: 'right',
    },
    left: {
        textAlign: 'left',
    },
    footerRow: {
        flexDirection: 'row',
        backgroundColor: '#b3b3b3',
        // borderTopWidth: 2, 
        // borderTopColor: DEFAULT_BORDER_COLOR_FOOTER,
        fontWeight: 'bold',
    },
    footerCell: {
        padding: 5,
        fontWeight: 'bold',
        fontSize: 9,
        justifyContent: 'center',
    },
});

const SalesTableGroupMonthMonthlyPDF = ({ rows, grandColumnTotals }) => {

    // --- 1. Replicating Dynamic Key Logic ---
    const descriptiveKey = useMemo(() => {
        if (!rows || rows.length === 0) return 'Code'; 
        const firstRowKeys = Object.keys(rows[0]);
        
        if (firstRowKeys.includes('Code')) return 'Code';
        if (firstRowKeys.includes('SGROUP')) return 'SGROUP';
        if (firstRowKeys.includes('GROUP')) return 'GROUP';
        if (firstRowKeys.includes('CATEGORY')) return 'CATEGORY';
        
        return 'Code';
    }, [rows]);

    const FIXED_COLUMNS = useMemo(() => {
        const firstRow = rows.length > 0 ? rows[0] : {};

        if (descriptiveKey === 'Code') {
            return ['Code', 'Description'];
        }
        if (firstRow.YEAR !== undefined) {
             return [descriptiveKey, 'YEAR'];
        }
        return [descriptiveKey];
    }, [descriptiveKey, rows]);
    // ----------------------------------------
    
    // --- 2. Width Calculation ---
    const totalColumns = FIXED_COLUMNS.length + MONTHLY_COLUMNS.length + 1; // Fixed + 12 Months + Total
    
    // Calculate widths. We allocate more width to descriptive columns (Fixed)
    const descriptiveWidth = FIXED_COLUMNS.length === 2 ? 35 : 25; // 35% for 2 fixed columns, 25% for 1
    const fixedColWidth = (descriptiveWidth / FIXED_COLUMNS.length).toFixed(2);
    
    // Remaining width is for 12 months + 1 total (13 columns)
    const remainingWidth = 100 - descriptiveWidth;
    const defaultMonthlyWidth = (remainingWidth / (MONTHLY_COLUMNS.length + 1)).toFixed(2); // 13 columns

    // --- 3. Footer Span Calculation ---
    const descriptiveSpan = FIXED_COLUMNS.length;
    const descriptiveFooterWidth = (descriptiveWidth).toFixed(2) + '%';
    
    const allDisplayKeys = [...FIXED_COLUMNS, ...MONTHLY_COLUMNS, 'TOTAL'];
    
    if (rows.length === 0 && (!grandColumnTotals || Object.keys(grandColumnTotals).length === 0)) {
        return (
            <Document>
                <Page size="A4" style={styles.page} orientation="landscape">
                    <Text style={{ textAlign: 'center', marginTop: 50 }}>No monthly data found.</Text>
                </Page>
            </Document>
        );
    }

    const renderHeader = () => {
        return (
            <View style={styles.headerRow} fixed>
                {/* Fixed Columns Header */}
                {FIXED_COLUMNS.map((columnKey, index) => {
                    const isLastFixed = index === FIXED_COLUMNS.length - 1;
                    const cellWidth = FIXED_COLUMNS.length === 2 ? (columnKey === 'Description' ? 25 : 10) : fixedColWidth; // Adjust width for description if two fixed
                    
                    return (
                        <View 
                            key={columnKey} 
                            style={[
                                styles.headerCell, 
                                styles.left, 
                                { 
                                    width: `${cellWidth}%`,
                                    // borderRightWidth: 1, 
                                    // borderRightColor: DEFAULT_BORDER_COLOR_HEADER,
                                }
                            ]}
                        >
                            <Text>{columnKey.toUpperCase()}</Text>
                        </View>
                    );
                })}

                {/* Monthly & Total Columns Header */}
                {[...MONTHLY_COLUMNS, 'TOTAL'].map((key, index) => {
                    const isLastCell = index === MONTHLY_COLUMNS.length;
                    return (
                        <View 
                            key={key} 
                            style={[
                                styles.headerCell, 
                                styles.numeric, 
                                { 
                                    width: `${defaultMonthlyWidth}%`,
                                    // borderRightWidth: isLastCell ? 0 : 1, // Last cell has no right border
                                    // borderRightColor: DEFAULT_BORDER_COLOR_HEADER,
                                }
                            ]}
                        >
                            <Text>{key.toUpperCase()}</Text>
                        </View>
                    );
                })}
            </View>
        );
    };

    const renderBody = () => (
        rows
            .filter(row => typeof row === 'object' && row !== null && Object.keys(row).length > 0)
            .map((row, index) => {
            return (
                <View key={`month-row-${index}`} style={styles.row} wrap={false}>
                    
                    {/* Fixed Data Cells */}
                    {FIXED_COLUMNS.map((columnKey, colIndex) => {
                         const cellWidth = FIXED_COLUMNS.length === 2 ? (columnKey === 'Description' ? 25 : 10) : fixedColWidth; 

                        return (
                            <View 
                                key={columnKey}
                                style={[
                                    styles.cell,
                                    styles.left,
                                    { 
                                        width: `${cellWidth}%`,
                                        // borderRightWidth: 1, 
                                        // borderRightColor: DEFAULT_BORDER_COLOR_BODY,
                                    }
                                ]}
                            >
                                <Text>{row[columnKey] === null || row[columnKey] === undefined ? 'N/A' : row[columnKey]}</Text>
                            </View>
                        );
                    })}
                    
                    {/* Monthly & Total Data Cells */}
                    {[...MONTHLY_COLUMNS, 'TOTAL'].map((key, index) => {
                        const isTotalColumn = key === 'TOTAL';
                        const isLastCell = index === MONTHLY_COLUMNS.length; // Last cell is TOTAL
                        
                        return (
                            <View 
                                key={key} 
                                style={[
                                    styles.cell, 
                                    styles.numeric, 
                                    isTotalColumn ? { fontWeight: 'bold' } : {},
                                    { 
                                        width: `${defaultMonthlyWidth}%`,
                                        // borderRightWidth: isLastCell ? 0 : 1, 
                                        // borderRightColor: DEFAULT_BORDER_COLOR_BODY,
                                    }
                                ]}
                            >
                                <Text>{formatNumber(row[key])}</Text>
                            </View>
                        );
                    })}
                </View>
            );
        })
    );

    const renderFooter = () => {
        const totalKeys = [...MONTHLY_COLUMNS, 'TOTAL'];

        return (
            <View style={styles.footerRow} fixed>
                {/* Descriptive Span for the GRAND TOTAL label */}
                <View 
                    style={[
                        styles.footerCell, 
                        styles.numeric, 
                        { 
                            width: descriptiveFooterWidth, // Use pre-calculated string width
                            // borderRightWidth: 1, 
                            // borderRightColor: DEFAULT_BORDER_COLOR_FOOTER,
                            textAlign: 'right', // Explicit right align for the label
                        }
                    ]}
                >
                    <Text>GRAND TOTAL:</Text>
                </View>
                
                {/* Total Value Cells */}
                {totalKeys.map((key, index) => {
                    const isLastCell = index === totalKeys.length - 1;
                    
                    return (
                        <View 
                            key={key} 
                            style={[
                                styles.footerCell, 
                                styles.numeric, 
                                { 
                                    width: `${defaultMonthlyWidth}%`,
                                    // borderRightWidth: isLastCell ? 0 : 1, 
                                    // borderRightColor: DEFAULT_BORDER_COLOR_FOOTER,
                                }
                            ]}
                        >
                            <Text>{formatNumber(grandColumnTotals[key] || 0)}</Text>
                        </View>
                    );
                })}
            </View>
        );
    };

    return (
        <Document>
            <Page size="A4" style={styles.page} orientation="landscape">
                <View style={styles.table}>
                    {renderHeader()}
                    {renderBody()}
                    {grandColumnTotals && Object.keys(grandColumnTotals).length > 0 && renderFooter()}
                </View>
            </Page>
        </Document>
    );
};

export default SalesTableGroupMonthMonthlyPDF;